import React, { useEffect, useRef, useState } from 'react';
import TunnelScene from './components/three/TunnelScene';
import SrPlaybackBar from './components/editor/SrPlaybackBar';
import RenderDebugOverlay from './components/editor/RenderDebugOverlay';
import LeftSidebar from './components/editor/LeftSidebar';
import RightSidebar from './components/editor/RightSidebar';
import FileManagerDialog from './components/editor/FileManagerDialog';
import FlashEventPopup from './components/editor/FlashEventPopup';
import ShapeDesignerPanel from './components/editor/ShapeDesignerPanel';
import BookmarkPopup from './components/editor/BookmarkPopup';
import { IcRewind, IcPlay, IcPause, IcPanels } from './components/ui/icons';
import { setSrHintBarForced } from './components/editor/SrHintBar';
import NewMapDialog from './components/editor/NewMapDialog';
import SettingsDialog from './components/editor/SettingsDialog';
import BenchmarkOverlay from './perf/BenchmarkOverlay';
import EditSongInfoDialog from './components/editor/EditSongInfoDialog';
import useKeybindingEngine from './hooks/useKeybindingEngine';
import { useIntensityCompute } from './hooks/useIntensityCompute';
import { usePatternDifficulty } from './hooks/usePatternDifficulty';
import IntensityDashboard from './components/editor/IntensityDashboard';
import PatternDifficultyMeter from './components/editor/PatternDifficultyMeter';
import PerfPill from './components/editor/PerfPill';
import VisualizerBackground from './viz/VisualizerBackground';
import { bg3dLayerStyle, font } from './components/ui/theme';
import { getSrAnalyserNode } from './audio/srAnalyser';
import { installTintHover } from './components/ui/tintHover';
import Dashboard from './Dashboard';
import FlowApp from './editors/flow/FlowApp';
import ResizeHandle from './components/ui/ResizeHandle';
import ScrollArrows from './components/ui/ScrollArrows';
import ScrollFade from './components/ui/ScrollFade';
import OverlayScrollbar from './components/ui/OverlayScrollbar';
import { useEditorStore } from './store/editorStore';
import { useAuthStore } from './store/authStore';
import { useTheme } from './components/ui/themeStore';
import MobilePlayer from './player/MobilePlayer';
import ErrorBoundary from './components/ErrorBoundary';
import LoginGate from './components/auth/LoginGate';
import { getBeatmap } from './api/client';
import { initTickAudio, setSrNoteSound } from './audio/tickSound';
import {
  setScrollSound, setScrollVolume, setPlaceSound, setPlaceVolume,
  setDeleteSound, setDeleteVolume, setFlowAudioVolume, setFlowAudioEnabled,
} from './editors/flow/flowSounds';
import { applySidebarX } from './components/ui/SidebarOffsetTuner';
import './App.css';

// Apply the saved sidebar horizontal offset before first paint, so it stays
// fixed across editors even if the tuning window isn't mounted.
applySidebarX();

// Mobile player route (QR-launched), decided at module load from the URL so a
// phone opening /play gets the standalone full-screen player, not the editor.
const IS_PLAY_ROUTE =
  typeof window !== 'undefined' && /(^|\/)play\b/.test(window.location.pathname);

// Fixed sidebar widths — identical to the Flow editor's grid columns
// (FlowApp gridTemplateColumns: `230px 1fr … 245px`) so all editors match.
// Sidebar widths are fluid CSS vars now (--sr-left-w / --sr-right-w in App.css,
// clamp()-based so they shrink on narrow viewports).

// URL ↔ activeView mapping. Paths: /flow, /synth-vr, /synth-console, / (dashboard)
const pathToView = (path: string) => {
  if (/^\/flow(\/|$)/.test(path)) return 'flow' as const;
  if (/^\/synth-vr(\/|$)/.test(path)) return 'synth-rider' as const;
  if (/^\/synth-console(\/|$)/.test(path)) return 'console' as const;
  return 'dashboard' as const;
};
const viewToPath = (view: string) => {
  if (view === 'flow') return '/flow';
  if (view === 'synth-rider') return '/synth-vr';
  if (view === 'console') return '/synth-console';
  return '/';
};

function App() {
  // Hooks must run unconditionally (rules-of-hooks), so read state first, then
  // branch. On the /play route the editor views never mount.
  const activeView = useEditorStore(s => s.activeView);
  const setActiveView = useEditorStore(s => s.setActiveView);

  // ── URL routing ─────────────────────────────────────────────────────────────
  // The store initializes activeView from the URL, so on the first run the URL
  // and activeView already agree — no history entry is added. On subsequent
  // changes we push so the browser back button works.
  const routingMounted = useRef(false);
  useEffect(() => {
    if (IS_PLAY_ROUTE) return;
    const path = viewToPath(activeView);
    if (window.location.pathname !== path) {
      // First effect run: normalize non-canonical paths (e.g. /dashboard → /)
      // without adding a history entry; after that, push new entries.
      if (!routingMounted.current) window.history.replaceState({}, '', path);
      else                          window.history.pushState({}, '', path);
    }
    routingMounted.current = true;
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the active editor's own accent colour (per-editor theming).
  useEffect(() => {
    useTheme.getState().setActiveEditor(activeView);
  }, [activeView]);

  // Browser back / forward → sync URL back into the store.
  useEffect(() => {
    if (IS_PLAY_ROUTE) return;
    const handler = () => setActiveView(pathToView(window.location.pathname));
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [setActiveView]);
  // ── End URL routing ──────────────────────────────────────────────────────────

  // Per-user app gate: editors are directly reachable by URL (/flow, /synth-vr,
  // /synth-console), so hiding a dashboard card isn't enough — bounce a
  // restricted user back to the dashboard if they land on an editor they're not
  // allowed to open. Admins and sessions without an apps list are unrestricted.
  const mapper = useAuthStore(s => s.mapper);
  useEffect(() => {
    if (IS_PLAY_ROUTE) return;
    const apps = mapper?.apps;
    if (mapper?.role === 'admin' || !apps) return;
    const GATED = ['flow', 'synth-rider', 'console'];
    if (GATED.includes(activeView) && !apps.includes(activeView)) {
      setActiveView('dashboard');
    }
  }, [activeView, mapper, setActiveView]);

  // Global tint-wash hover for every button across ALL editors. Installed at the
  // root so Flow / Console / Synth VR all get the identical hover (it's a single
  // document-level listener — see tintHover.ts). Previously this lived inside the
  // Synth VR view, so Flow & Console buttons had no hover wash.
  useEffect(() => installTintHover(), []);

  // Top-level router. Each branch mounts a completely independent view so
  // hooks (WebSockets, AI fetches, etc.) only run for the editor that's
  // actually on screen. Wrapped in an ErrorBoundary so a thrown error (e.g. a
  // failed model/asset load surfacing through Suspense) shows a diagnosable
  // message instead of silently blank-screening the whole app.
  // The /play route is a public, QR-launched player — never gate it. Every
  // other view sits behind the Google sign-in gate (bypassed on localhost).
  if (IS_PLAY_ROUTE) return <ErrorBoundary><MobilePlayer /></ErrorBoundary>;

  let view: React.ReactNode;
  if (activeView === 'dashboard') view = <Dashboard />;
  else if (activeView === 'flow') view = <FlowApp />;
  // Synth Console is being rebuilt on top of the Synth VR editor: it now REUSES
  // the exact same shell + components + store (full parity), differing only by
  // `activeView` (drives the title/back + future console-specific behaviour). The
  // previous Flow-based Console editor is preserved in _console_backup_20260629/.
  else view = <SynthRiderApp />;

  return (
    <ErrorBoundary>
      <LoginGate>{view}</LoginGate>
      {/* Layout tuner hidden — tuned values are applied via applySidebarX() at
          module load. Re-add <SidebarOffsetTuner /> here to tweak them again. */}
    </ErrorBoundary>
  );
}

// 0..1 energy from chart notes near the playhead — drives the visualizer's
// "React to: Notes" mode. Pulses to 1 right on a note, decaying over ~1/4 beat.
function getSrNoteEnergy(): number {
  const st = useEditorStore.getState();
  const diff = st.beatmap?.difficulties[st.activeDifficulty];
  if (!diff) return 0;
  const bpm = st.beatmap?.bpm || 120;
  const rawBeat = (st.playback.currentTime / 60) * bpm;
  let best = Infinity;
  for (const n of diff.notes) {
    const d = Math.abs(n.time_beats - rawBeat);
    if (d < best) best = d;
    if (best < 0.01) break;
  }
  return Math.max(0, 1 - best / 0.25);
}

function SynthRiderApp() {
  const beatmap = useEditorStore(s => s.beatmap);
  // Collapse states
  const leftSidebarOpen = useEditorStore(s => s.leftSidebarOpen);
  const rightSidebarOpen = useEditorStore(s => s.rightSidebarOpen);
  const bottomPanelOpen = useEditorStore(s => s.bottomPanelOpen);
  const setLeftSidebarOpen = useEditorStore(s => s.setLeftSidebarOpen);
  const setRightSidebarOpen = useEditorStore(s => s.setRightSidebarOpen);
  const setBottomPanelOpen = useEditorStore(s => s.setBottomPanelOpen);
  const uiHidden = useEditorStore(s => s.uiHidden);
  const setUiHidden = useEditorStore(s => s.setUiHidden);
  const projectSaved = useEditorStore(s => s.projectSaved);
  // Collapsed-bar transport (works while the playback bar is hidden).
  const pbPlaying = useEditorStore(s => s.playback.playing);
  const setPlaying = useEditorStore(s => s.setPlaying);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);

  // Sidebar widths are FIXED to match Flow exactly (no horizontal resize).
  const bottomPanelHeight = useEditorStore(s => s.bottomPanelHeight);
  const setBottomPanelHeight = useEditorStore(s => s.setBottomPanelHeight);

  // Scroll containers for the floating jump-to-top/bottom arrows.
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const rightSidebarRef = useRef<HTMLDivElement>(null);

  // Mirror the SFX preferences (scroll / place / delete voices + volumes) into
  // the shared sound module so changes in Settings take effect immediately.
  const gameplaySettings = useEditorStore(s => s.gameplaySettings);
  const sfxVolume = useEditorStore(s => s.sfxVolume);
  useEffect(() => {
    setFlowAudioEnabled(true);
    setFlowAudioVolume(sfxVolume);
    setScrollSound(gameplaySettings.scrollSound);
    setScrollVolume(gameplaySettings.scrollVolume);
    setPlaceSound(gameplaySettings.placeSound);
    setPlaceVolume(gameplaySettings.placeVolume);
    setDeleteSound(gameplaySettings.deleteSound);
    setDeleteVolume(gameplaySettings.deleteVolume);
    // The SR note hit is produced by tickSound (its own context), so the voice
    // choice goes there — the shared module only owns place/delete/scroll here.
    setSrNoteSound(gameplaySettings.noteSound ?? 'tone');
  }, [gameplaySettings, sfxVolume]);

  // Contextual arrow keys: when notes are selected, ←/→ move them in X and
  // ↑/↓ in Y (one grid cell), intercepting BEFORE the keybinding engine so the
  // timeline doesn't also scrub. With nothing selected, arrows fall through to
  // their normal timeline navigation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const st = useEditorStore.getState();
      if (st.selectedNoteIds.length === 0) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -1;
      else if (e.key === 'ArrowRight') dx = 1;
      else if (e.key === 'ArrowUp') dy = 1;
      else if (e.key === 'ArrowDown') dy = -1;
      else return;
      e.preventDefault();
      e.stopPropagation();
      st.nudgeSelection(dx, dy);
    };
    window.addEventListener('keydown', onKey, true); // capture: before keybinding engine
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const intensityData = useEditorStore(s => s.intensityData);
  const patternDifficultyVisible = useEditorStore(s => s.patternDifficultyVisible);

  // Data-driven keybinding engine
  useKeybindingEngine();

  // Cursor feedback for drag mode and tool modes
  const draggingNoteIndex = useEditorStore(s => s.draggingNoteIndex);
  const toolMode = useEditorStore(s => s.toolMode);
  useEffect(() => {
    if (draggingNoteIndex !== null) {
      document.body.style.cursor = 'grabbing';
    } else if (toolMode === 'eraser') {
      document.body.style.cursor = 'crosshair';
    } else {
      document.body.style.cursor = '';
    }
    return () => { document.body.style.cursor = ''; };
  }, [draggingNoteIndex, toolMode]);

  // Pre-compute intensity data when beatmap / difficulty changes
  useIntensityCompute();

  // Refresh hand-aware Pattern Difficulty report from backend
  usePatternDifficulty();

  // Sync stem mixer weights via WebSocket

  // Warm up tick audio context on first user interaction
  useEffect(() => { initTickAudio(); }, []);

  // Auto-reload each editor's OWN last-opened map (VR ⇄ Console independent).
  // Runs on mount and whenever you switch editors: if the current editor has no
  // live map but remembers one, fetch + load it from the backend.
  const activeView = useEditorStore(s => s.activeView);
  const loadBeatmap = useEditorStore(s => s.loadBeatmap);
  // Boot preloader — true while the initial map fetch is in flight, so the empty
  // viewport shows a spinner instead of a few seconds of blank black.
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    if (activeView !== 'synth-rider' && activeView !== 'console') { setBooting(false); return; }
    const st = useEditorStore.getState();
    if (st.beatmap) { setBooting(false); return; } // this editor already shows its map
    const byEd = st.lastBeatmapIdByEditor || {};
    // Use this editor's own remembered id. Fall back to the legacy single id ONLY
    // during first-run migration (no per-editor ids yet) so we never load one
    // editor's map into the other.
    const anyPerEditor = !!(byEd['synth-rider'] || byEd['console']);
    const id = byEd[activeView] ?? (anyPerEditor ? undefined : st.lastBeatmapId ?? undefined);
    if (!id) { setBooting(false); return; }   // nothing to auto-load → no spinner
    setBooting(true);
    getBeatmap(id)
      .then((bm) => {
        const s2 = useEditorStore.getState();
        // Only apply if still on this editor and still empty (avoid races).
        if (!s2.beatmap && s2.activeView === activeView) loadBeatmap(bm);
      })
      .catch(() => { /* backend may not have it anymore — ignore */ })
      .finally(() => setBooting(false));
  }, [activeView, loadBeatmap]);

  // Bottom panel resize range. With the training console hidden (current SR),
  // the Timeline content is the top handle (≈12px) + transport bar (≈47px) +
  // waveform canvas (100px). So the panel can shrink to show ONLY the transport
  // controls (waveform clipped by overflow:hidden) and grows at most to the full
  // content height — never leaving empty space below the waveform.
  const TRANSPORT_ONLY_H = 60;
  const FULL_CONTENT_H = 160;
  const panelMin = TRANSPORT_ONLY_H;
  const panelMax = FULL_CONTENT_H;
  const panelHeight = Math.min(panelMax, Math.max(panelMin, bottomPanelHeight));

  return (
    <div className="app">
      {/* Main content area — sidebars extend full height, timeline between them.
          The sidebar widths are exposed as CSS vars so the bottom panel can sit
          in the gap between them (and slide when they resize / collapse). */}
      <div
        className={`app-main${uiHidden ? ' ui-hidden' : ''}`}
        style={{
          // Sidebar widths are FLUID (clamp() vars in App.css — shrink on
          // narrow viewports) + the shared layout-tuner vars (margin-left,
          // gap, sidebar-x). The bottom panel sits in the gap between the two
          // sidebars (and goes full-width below 1100px via media query).
          '--bottom-left': leftSidebarOpen
            ? 'calc(var(--shell-pad-left, 12px) + var(--sr-left-w) + var(--shell-gap, 3px))'
            : 'var(--shell-pad-left, 12px)',
          '--bottom-right': rightSidebarOpen
            ? 'calc(var(--sidebar-x, 12px) + var(--sr-right-w) + var(--shell-gap, 3px))'
            : 'var(--sidebar-x, 12px)',
        } as React.CSSProperties}
      >
        {/* Left sidebar: controls — full height, resizable */}
        <div
          className={`sidebar-left${leftSidebarOpen ? '' : ' collapsed'}`}
          style={leftSidebarOpen ? { width: 'var(--sr-left-w)', minWidth: 'var(--sr-left-w)' } : undefined}
        >
          <button
            className="sidebar-toggle sidebar-toggle-left"
            onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
            title={leftSidebarOpen ? 'Collapse left sidebar' : 'Expand left sidebar'}
          >
            {leftSidebarOpen ? '◀' : '▶'}
          </button>
          {leftSidebarOpen && <LeftSidebar scrollRef={leftSidebarRef} />}
          {/* Scroll overlays portal to <body> (position:fixed), so the focus-mode
              slide transform can't move them — hide them outright while hidden. */}
          {leftSidebarOpen && !uiHidden && <ScrollArrows targetRef={leftSidebarRef} />}
          {leftSidebarOpen && !uiHidden && <ScrollFade targetRef={leftSidebarRef} />}
          {leftSidebarOpen && !uiHidden && <OverlayScrollbar targetRef={leftSidebarRef} side="left" rightInset={5} />}
        </div>

        {/* Center column: 3D view + timeline */}
        <div className="center-column">
          <div className="viewport" style={{ position: 'relative', isolation: 'isolate' }}>
            {beatmap ? (
              <>
                {/* Shared visualizer scene renders BEHIND the (transparent)
                    highway canvas, driven by the playhead + live audio / notes. */}
                <div style={{ position: 'absolute', inset: 0, zIndex: -1, pointerEvents: 'none', overflow: 'hidden' }}>
                  {/* Flow's full-screen DOM gradient backdrop (shared token) — the
                      3D canvas is transparent so this shows through. */}
                  <div style={bg3dLayerStyle} />
                  <VisualizerBackground
                    getTime={() => useEditorStore.getState().playback.currentTime}
                    getAnalyser={getSrAnalyserNode}
                    getNoteEnergy={getSrNoteEnergy}
                  />
                </div>
                <TunnelScene />
                {/* RenderModeSwitch moved into Settings → UI → Rendering. */}
                {heatmapEnabled && intensityData && <IntensityDashboard />}
                {/* Select/Eraser overlay removed — like Flow, hold Alt to
                    select and Ctrl to erase. */}
                {patternDifficultyVisible && (
                  <div
                    style={{
                      position: 'absolute',
                      // Anchored to the BOTTOM of the viewport (near the transport
                      // controls) instead of floating at the top.
                      bottom: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 'min(760px, calc(100% - 32px))',
                      zIndex: 12,
                      // pointerEvents passes through to the 3D viewport,
                      // but the meter itself overrides this via pointerEvents:'auto'
                      // so the heatmap click-to-seek still works.
                      pointerEvents: 'none',
                    }}
                  >
                    <PatternDifficultyMeter />
                  </div>
                )}
                {/* Performance pill — live FPS + density mode (Auto/Perf/Quality),
                    anchored top-centre of the viewport. */}
                <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 13, pointerEvents: 'auto' }}>
                  <PerfPill />
                </div>
              </>
            ) : null /* Welcome/empty screen removed — the editor auto-loads the
                 last map and goes straight in; no big-icon splash. */}

            {/* Boot preloader — fills the viewport while the initial map is being
                fetched, so it's never a few seconds of blank black. Hides as soon
                as the map loads (the floor/grid render immediately after). */}
            {booting && !beatmap && (
              <div className="viewport-loader">
                <div className="viewport-spinner" />
                <div className="viewport-loader-text">Loading map…</div>
              </div>
            )}
          </div>

          {/* Bottom panel: Timeline + Training console — resizable */}
          <div
            className={`bottom-panel${bottomPanelOpen ? '' : ' collapsed'}`}
            // The training console is hidden, so the panel only ever holds the
            // fixed-height hint + playback + waveform — always auto-size it so it
            // sits flush at the bottom (bottom:16px) with no gap below.
            style={undefined}
          >
            {/* "Project saved" toast — anchored to the playback bar's top-left
                (aligned with its left margin, one object-gap above it) instead of
                floating centre-screen. */}
            {projectSaved && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, zIndex: 30,
                padding: '9px 18px', borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: font.sans,
                color: '#fff', background: 'oklch(0.5 0.14 150 / 0.92)',
                border: '1px solid oklch(0.7 0.18 150 / 0.5)', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
                pointerEvents: 'none', whiteSpace: 'nowrap',
              }}>✓ Project saved</div>
            )}
            {/* Expanded: thin hover handle to collapse. Collapsed: a compact
                accent transport (rewind · play/pause) + a "Show playback" pill —
                so playback still works with the bar hidden. */}
            {bottomPanelOpen ? (
              <button
                className="bottom-panel-handle"
                onClick={() => setBottomPanelOpen(false)}
                aria-label="Hide playback bar"
                data-no-tint-hover
                onMouseEnter={() => setSrHintBarForced(true)}
                onMouseLeave={() => setSrHintBarForced(false)}
              />
            ) : (
              <div className="bottom-panel-collapsed">
                <button
                  className="ct-btn"
                  onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setCurrentTime(0); }}
                  title="Rewind to start"
                ><IcRewind size={14} /></button>
                <button
                  className={pbPlaying ? 'ct-btn accent-fill-active' : 'ct-btn'}
                  onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setPlaying(!pbPlaying); }}
                  title="Play / Pause"
                >{pbPlaying ? <IcPause size={14} /> : <IcPlay size={14} />}</button>
                <button
                  className="bottom-panel-show"
                  onClick={() => setBottomPanelOpen(true)}
                  title="Show the full playback bar"
                >▴ Show playback</button>
              </div>
            )}
            {/* Always mount the playback bar so the audio engine + play/pause stay
                alive even while collapsed; just hide it visually. */}
            {beatmap && (
              <div
                style={bottomPanelOpen ? undefined : { height: 0, overflow: 'hidden', pointerEvents: 'none' }}
                aria-hidden={!bottomPanelOpen}
              >
                <SrPlaybackBar />
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar: tools + properties — resizable */}
        <div
          className={`sidebar-right${rightSidebarOpen ? '' : ' collapsed'}`}
          style={rightSidebarOpen ? { width: 'var(--sr-right-w)', minWidth: 'var(--sr-right-w)' } : undefined}
        >
          <button
            className="sidebar-toggle sidebar-toggle-right"
            onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
            title={rightSidebarOpen ? 'Collapse right sidebar' : 'Expand right sidebar'}
          >
            {rightSidebarOpen ? '▶' : '◀'}
          </button>
          {rightSidebarOpen && <RightSidebar scrollRef={rightSidebarRef} />}
          {/* Scroll overlays portal to <body> — hide while focus-mode hidden. */}
          {rightSidebarOpen && !uiHidden && <ScrollArrows targetRef={rightSidebarRef} />}
          {rightSidebarOpen && !uiHidden && <ScrollFade targetRef={rightSidebarRef} />}
          {rightSidebarOpen && !uiHidden && <OverlayScrollbar targetRef={rightSidebarRef} side="right" rightInset={5} />}
        </div>
      </div>

      {/* Focus mode — floating "show panels" button, lives OUTSIDE .app-main so it
          stays put while the panels slide away. Fades in when the UI is hidden. */}
      <button
        className={`ui-restore-btn${uiHidden ? ' show' : ''}`}
        onClick={() => setUiHidden(false)}
        title="Show panels"
        aria-hidden={!uiHidden}
      >
        <IcPanels size={18} />
      </button>

      {/* Modals */}
      <FileManagerDialog />
      <NewMapDialog />
      <SettingsDialog />
      <BenchmarkOverlay />
      <EditSongInfoDialog />
      <FlashEventPopup />
      <BookmarkPopup />
      <ShapeDesignerPanel />

      {/* Live render/scene debug HUD (bottom-right) — toggle with ` */}
      <RenderDebugOverlay />
    </div>
  );
}

export default App;
